/**
 * Active CRM object definitions (Clarix CRM dynamic layer). The CRM app's object
 * set is workspace-scoped: `/crm/api/objects` returns only the ACTIVE objects.
 * The shell uses this to (a) hide CRM sidebar entries whose object the admin
 * deactivated, and (b) render dynamic sidebar entries for CUSTOM (admin-defined)
 * objects, which have no hardcoded nav item.
 *
 * Fail-OPEN: while loading, on error, or when the caller lacks masterconfig.view
 * (403), the hooks return null/[] and callers show every hardcoded entry — nav
 * must never break because this optional gate couldn't resolve. The shell rail
 * waits out only the first load (`useCrmObjectsPending`), never an error.
 */

import { useQuery } from '@tanstack/react-query';
import { apiService } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';

interface CrmObjectSummary {
  api_name: string;
  label: string;
  icon: string | null;
  kind: string;
  backing_table: string | null;
}

function useCrmObjectsRaw(enabled = true) {
  const hasToken = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['crm', 'objects'],
    queryFn: () => apiService.get<CrmObjectSummary[]>('/crm/api/objects'),
    enabled: hasToken && enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  });
}

/** True only while the first object fetch is in flight; an error or a 403 settles it. */
export function useCrmObjectsPending(enabled = true): boolean {
  const { isPending, fetchStatus } = useCrmObjectsRaw(enabled);
  return isPending && fetchStatus !== 'idle';
}

/** Set of ACTIVE CRM object api_names, or `null` when unresolved (fail-open). */
export function useActiveCrmObjectNames(enabled = true): Set<string> | null {
  const { data } = useCrmObjectsRaw(enabled);
  return data ? new Set(data.map((o) => o.api_name)) : null;
}

export interface CustomObjectLink {
  api_name: string;
  label: string;
  icon: string | null;
}

/**
 * Active CUSTOM objects (admin-defined, JSONB-backed) — they have no hardcoded
 * sidebar entry, so the CRM nav renders them dynamically → `/crm/o/:apiName`.
 * Standard objects with a real backing table are excluded (they have their own
 * dedicated pages/nav).
 */
export function useCustomCrmObjects(enabled = true): CustomObjectLink[] {
  const { data } = useCrmObjectsRaw(enabled);
  return (data ?? [])
    .filter((o) => o.kind === 'CUSTOM' && !o.backing_table)
    .map((o) => ({ api_name: o.api_name, label: o.label, icon: o.icon }));
}

// Listing and deleting objects belong to the CRM Configuration page (the remote's own
// `objects.query.ts`). The remote mounts its own QueryClient, so its invalidation never
// reaches `['crm','objects']` here: the shell's copy catches up on its `staleTime`.
